import { Component, createRef } from 'react';
import { CssGeneratorLayout, cssGenCommon as genStyles } from '../converter-layout/css-generator-layout';

interface State {
    title: string;
    subtitle: string;
    siteName: string;
    bgColor: string;
    textColor: string;
    accentColor: string;
    fontSize: number;
    align: 'left' | 'center';
}

const DEFAULT: State = {
    title: 'Your Article Title Goes Here',
    subtitle: 'A subtitle that adds context to the headline above.',
    siteName: 'xconvert.com',
    bgColor: '#1A1F36',
    textColor: '#FFFFFF',
    accentColor: '#635BFF',
    fontSize: 64,
    align: 'left',
};

const W = 1200;
const H = 630;

export default class OgImageGeneratorComponent extends Component<{ feature?: string }, State> {
    private canvasRef = createRef<HTMLCanvasElement>();
    state: State = { ...DEFAULT };

    componentDidMount() {
        this.draw();
    }

    componentDidUpdate() {
        this.draw();
    }

    private draw() {
        const canvas = this.canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const { title, subtitle, siteName, bgColor, textColor, accentColor, fontSize, align } = this.state;

        // Background
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, W, H);

        // Accent bar
        ctx.fillStyle = accentColor;
        ctx.fillRect(0, 0, 12, H);

        // Title
        ctx.fillStyle = textColor;
        ctx.font = `bold ${fontSize}px -apple-system, "Segoe UI", Roboto, sans-serif`;
        ctx.textBaseline = 'top';
        const titleX = align === 'center' ? W / 2 : 80;
        ctx.textAlign = align === 'center' ? 'center' : 'left';
        const titleLines = this.wrapText(ctx, title, W - 160);
        let y = 120;
        for (const line of titleLines.slice(0, 3)) {
            ctx.fillText(line, titleX, y);
            y += fontSize * 1.2;
        }

        // Subtitle
        if (subtitle) {
            ctx.font = `400 ${Math.round(fontSize * 0.5)}px -apple-system, "Segoe UI", Roboto, sans-serif`;
            ctx.fillStyle = textColor;
            ctx.globalAlpha = 0.75;
            const subLines = this.wrapText(ctx, subtitle, W - 160);
            for (const line of subLines.slice(0, 2)) {
                ctx.fillText(line, titleX, y + 24);
                y += Math.round(fontSize * 0.5 * 1.3);
            }
            ctx.globalAlpha = 1;
        }

        // Site name at bottom
        if (siteName) {
            ctx.fillStyle = accentColor;
            ctx.font = `600 ${Math.round(fontSize * 0.4)}px -apple-system, "Segoe UI", Roboto, sans-serif`;
            ctx.textAlign = align === 'center' ? 'center' : 'left';
            ctx.fillText(siteName, titleX, H - 80);
        }
    }

    private wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
        const words = text.split(/\s+/);
        const lines: string[] = [];
        let current = '';
        for (const w of words) {
            const test = current ? `${current} ${w}` : w;
            if (ctx.measureText(test).width > maxWidth) {
                if (current) lines.push(current);
                current = w;
            } else {
                current = test;
            }
        }
        if (current) lines.push(current);
        return lines;
    }

    download = () => {
        const canvas = this.canvasRef.current;
        if (!canvas) return;
        canvas.toBlob(blob => {
            if (!blob) return;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'og-image.png';
            a.click();
            URL.revokeObjectURL(url);
        }, 'image/png');
    };

    private set(key: keyof State, val: any) {
        this.setState({ [key]: val } as any);
    }

    render() {
        const s = this.state;

        const controls = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Content</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="og-title">Title</label>
                        <input id="og-title" type="text" value={s.title} onChange={e => this.set('title', e.target.value)} />
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="og-sub">Subtitle</label>
                        <textarea id="og-sub" rows={2} value={s.subtitle} onChange={e => this.set('subtitle', e.target.value)} />
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="og-site">Site / brand name</label>
                        <input id="og-site" type="text" value={s.siteName} onChange={e => this.set('siteName', e.target.value)} />
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Colors</span></div>
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="og-bg">Background</label>
                            <input id="og-bg" type="color" value={s.bgColor} style={{ height: 38, padding: 4 }}
                                onChange={e => this.set('bgColor', e.target.value)} />
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="og-text">Text</label>
                            <input id="og-text" type="color" value={s.textColor} style={{ height: 38, padding: 4 }}
                                onChange={e => this.set('textColor', e.target.value)} />
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="og-accent">Accent</label>
                            <input id="og-accent" type="color" value={s.accentColor} style={{ height: 38, padding: 4 }}
                                onChange={e => this.set('accentColor', e.target.value)} />
                        </div>
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Layout</span></div>
                    <div className={genStyles.field}>
                        <label>Title size: <strong>{s.fontSize}px</strong></label>
                        <input type="range" min={32} max={96} step={4} value={s.fontSize}
                            onChange={e => this.set('fontSize', +e.target.value)} />
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="og-align">Alignment</label>
                        <select id="og-align" value={s.align} onChange={e => this.set('align', e.target.value as any)}>
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                        </select>
                    </div>
                </div>
            </>
        );

        return (
            <CssGeneratorLayout
                controls={controls}
                previewLabel="Preview"
                previewMeta="1200 × 630 PNG"
                preview={
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <canvas ref={this.canvasRef} width={W} height={H}
                            style={{ maxWidth: '100%', height: 'auto', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                            aria-label="OG image preview" />
                    </div>
                }
                codeLabel="HTML / OpenGraph"
                code={`<meta property="og:image" content="https://your-site.com/og-image.png" />\n<meta property="og:image:width" content="${W}" />\n<meta property="og:image:height" content="${H}" />`}
                codeActions={
                    <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`} onClick={this.download}>
                        ⬇ Download 1200×630 PNG
                    </button>
                }
            />
        );
    }
}
